import {
    useLayoutEffect,
    useEffect,
    useState,
    useRef
} from "react";
import {
    TouchableOpacity,
    Platform,
    Image,
    View
} from "react-native";
import type {
    SelectedFileType
} from "./type";
import stylesheet from "./stylesheet";
import PositionCard from "./components/positionCard";
import VerifyMailCode from "./components/verifyMail";
import {
    type IBottomSheetRef,
    NCoreUIKitLocalize,
    NCoreUIKitSnackBar,
    NCoreUIKitToast,
    NCoreUIKitTheme,
    PageContainer,
    TextAreaInput,
    BottomSheet,
    TextInput,
    Button,
    Dialog,
    Text
} from "ncore-ui-kit";
import {
    handleUpload,
    useDebouncedDimensions
} from "../../../utils";
import {
    useNavigation,
    useRoute,
    type RouteProp
} from "@react-navigation/native";
import {
    useSafeAreaInsets
} from "react-native-safe-area-context";
import type {
    GetMyProfilePayloadType
} from "../../../services/restAPI/actions/getMyProfile/types";
import type {
    GetProfileUpdateRequestPayloadType
} from "../../../services/restAPI/actions/getProfileUpdateRequests/types";
import type {
    SendProfileUpdateRequestRequestType
} from "../../../services/restAPI/actions/sendProfileUpdateRequest/types";
import type AppStackParamList from "../../../types/navigation/app";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    CircleQuestionMark,
    ChevronLeft,
    AlertCircle,
    CircleAlert,
    Check,
    Pencil
} from "lucide-react-native";
import ImageCropPicker from "react-native-image-crop-picker";
import validator from "validator";
import {
    RESTService
} from "../../../services/restAPI";

const EditProfile = () => {
    const navigation = useNavigation<NativeStackNavigationProp<AppStackParamList>>();

    const route = useRoute<RouteProp<AppStackParamList, "EditProfile">>();
    const {
        windowHeight
    } = useDebouncedDimensions();

    const {
        user
    } = route.params;

    const {
        spaces,
        borders,
        radiuses,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const inset = useSafeAreaInsets();

    const [data,
        setData] = useState<GetMyProfilePayloadType>(user);
    const [isProfileUpdateRequest,
        setIsProfileUpdateRequest] = useState<GetProfileUpdateRequestPayloadType>();
    const [updateParams,
        setUpdateParams] = useState<SendProfileUpdateRequestRequestType>();
    const [successDialog,
        setSuccessDialog] = useState(false);
    const [verificationDialog,
        setVerificationDialog] = useState(false);
    const [selectedImage,
        setSelectedImage] = useState<string | null>(null);
    const [selctedFile,
        setSelectedFile] = useState<SelectedFileType | null>(null);

    const verifyMailRef = useRef<IBottomSheetRef>(null);

    useEffect(() => {
        getProfileUpdateRequest();
    }, []);

    useLayoutEffect(() => {
        navigation.setOptions({
            headerShown: true,
            header: () => <View
                style={[
                    stylesheet.headerContainer,
                    {
                        paddingTop: inset.top,
                        paddingHorizontal: spaces.spacingMd,
                        backgroundColor: colors.content.container.default
                    }
                ]}
            >
                <TouchableOpacity
                    style={[
                        stylesheet.backButton,
                        {
                            padding: spaces.spacingSm
                        }
                    ]}
                    onPress={() => {
                        navigation.goBack();
                    }}
                >
                    <ChevronLeft
                        size={30}
                    />
                </TouchableOpacity>
                <View
                    style={[
                        stylesheet.headerTitleContainer,
                        {
                            top: inset.top + spaces.spacingSm
                        }
                    ]}
                >
                    <Text
                        variant="titleLargeSize"
                        color="high"
                        style={stylesheet.headerTitle}
                    >
                        {localize("editProfile")}
                    </Text>
                </View>
            </View>
        });
    }, [navigation]);

    const getProfileUpdateRequest = () => {
        RESTService.action("GetProfileUpdateRequest", null)
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    setIsProfileUpdateRequest(res.payload);
                    NCoreUIKitSnackBar.open({
                        title: localize("pendingRequestExists"),
                        type: "danger",
                        isShowAction: false,
                        autoCloseDelay: 10000,
                        icon: ({
                            color
                        }) => <CircleAlert
                            color={colors.content.icon[color ? color : "default"]}
                        />
                    });
                }
            }).catch((err) => {
                console.log(err);
            });
    };

    const startMailVerify = () => {
        RESTService.action("StartMailVerify", null)
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {

                    verifyMailRef.current?.open();
                }
            }).catch((err) => {
                console.log("err", err);
            });
    };

    const handleUpdateProfilePhoto = () => {
        if(!selctedFile) {
            return;
        }

        RESTService.action("UploadProfilePhoto", null)
            .then(async (res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {

                    await handleUpload(selctedFile, res.payload.uploadToken)
                        .then(() => {
                            setSuccessDialog(true);
                        })
                        .catch((err) => {
                            console.log("err", err);
                            throw err;
                        });
                }
            }).catch((err) => {
                console.log("err", err);
            });
    };

    const sendProfileUpdateReq = () => {
        if(!updateParams) {
            if(selctedFile) {
                handleUpdateProfilePhoto();
                return;
            }

            NCoreUIKitToast.open({
                title: localize("emptyRequestError"),
                isShowAction: false,
                type: "warning",
                icon: ({
                    color
                }) => <AlertCircle
                    color={colors.content.icon[color ? color : "default"]}
                />
            });
            return;
        }
        const params: SendProfileUpdateRequestRequestType = {};

        if(updateParams.aboutMyTrade && updateParams.aboutMyTrade.length && updateParams.aboutMyTrade.trim() !== user.aboutMyTrade.trim()) {
            params.aboutMyTrade = updateParams.aboutMyTrade;
        }

        if(updateParams.firstName && updateParams.firstName.length && updateParams.firstName.trim() !== user.firstName.trim()) {
            params.firstName = updateParams.firstName;
        }

        if(updateParams.lastName && updateParams.lastName.length && updateParams.lastName.trim() !== user.lastName.trim()) {
            params.lastName = updateParams.lastName;
        }

        if(updateParams.phoneNumber && updateParams.phoneNumber.length && updateParams.phoneNumber.trim() !== user.phoneNumber.trim() && validator.isMobilePhone(updateParams.phoneNumber)) {
            params.phoneNumber = updateParams.phoneNumber;
        }

        if(updateParams.mail && updateParams.mail.length && updateParams.mail.trim() !== user.mail.trim() && validator.isEmail(updateParams.mail)) {
            params.mail = updateParams.mail;
        }

        if(updateParams.companies && updateParams.companies.length) {
            params.companies = updateParams.companies;
        }

        if (Object.keys(params).length === 0) {
            if(selctedFile) {
                handleUpdateProfilePhoto();
                return;
            }
            NCoreUIKitToast.open({
                title: localize("emptyRequestError"),
                isShowAction: false,
                type: "warning",
                icon: ({
                    color
                }) => <AlertCircle
                    color={colors.content.icon[color ? color : "default"]}
                />
            });
            return;
        };

        RESTService.action("SendProfileUpdateRequest", params)
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    if(selctedFile) {
                        handleUpdateProfilePhoto();
                        return;
                    }
                    setSuccessDialog(true);
                }
            }).catch((err) => {
                console.log(err);
            });
    };

    const renderProfilePhoto = () => {
        return <View
            style={[
                stylesheet.profilePhotoWrapper,
                {
                    borderRadius: radiuses.full,
                    marginTop: spaces.spacingLg,
                    marginBottom: spaces.spacingXl
                }
            ]}
        >
            <Image
                source={{
                    uri: selectedImage ? selectedImage : data.profilePhotoURL
                }}
                resizeMode="cover"
                style={[
                    stylesheet.profilePhotoImage,
                    {
                        borderRadius: radiuses.full
                    }
                ]}
            />
            <TouchableOpacity
                style={[
                    stylesheet.photoEditButton,
                    {
                        right: -spaces.spacingSm,
                        bottom: -spaces.spacingSm,
                        backgroundColor: colors.content.container.mid,
                        borderRadius: radiuses.full,
                        padding: spaces.spacingSm,
                        borderWidth: borders.subtract,
                        borderColor: colors.content.border.subtle
                    }
                ]}
                disabled={isProfileUpdateRequest ? true : false}
                onPress={() => {
                    ImageCropPicker.openPicker({
                        cropping: true,
                        multiple: false
                    }).then(image => {
                        const format = image.mime?.split("/")[1];

                        if (image.size > 20 * 1024 * 1024) {
                            NCoreUIKitToast.open({
                                title: localize("imageTooLarge"),
                                type: "warning"
                            });
                            return;
                        }

                        if (![
                            "jpg",
                            "jpeg",
                            "png",
                            "heic"
                        ].includes(format as string)) {
                            NCoreUIKitToast.open({
                                title: localize("invalidFormat"),
                                type: "warning"
                            });
                            return;
                        }

                        const selectedFile = {
                            name: image.path.split("/").pop(),
                            type: image.mime,
                            uri: Platform.OS === "ios" ? image.path.startsWith("file://") ? image.path : `file:/${image.path}` : image.path
                        } as unknown as { name: string; type: string; uri: string; };
                        setSelectedImage(image.path);
                        setSelectedFile(selectedFile);
                    }).catch((err) => {
                        console.error(err);
                    });
                }}
            >
                <Pencil
                    size={20}
                />
            </TouchableOpacity>
        </View>;
    };

    const isFormValid = Boolean(
        data.firstName && data.firstName.trim().length > 0 &&
        data.lastName && data.lastName.trim().length > 0 &&
        data.mail && validator.isEmail(data.mail) &&
        (!data.phoneNumber || validator.isMobilePhone(data.phoneNumber))
    );

    const renderGeneralInfo = () => {
        return <View>
            <Text
                variant="titleLargeSize"
                color="high"
                style={{
                    marginBottom: spaces.spacingMd
                }}
            >
                {localize("generalInformation")}
            </Text>
            <TextInput
                title={localize("firstName")}
                type={updateParams?.firstName !== undefined && updateParams.firstName.trim() === "" ? "danger" : "default"}
                hintText={updateParams?.firstName !== undefined && updateParams.firstName.trim() === "" ? (localize("required-field")) : undefined}
                spreadBehaviour="stretch"
                placeholder={data.firstName}
                defaultValue={data.firstName}
                isDisabled={isProfileUpdateRequest ? true: false}
                onChangeText={(e) => {
                    setData({
                        ...data,
                        firstName: e
                    });
                    setUpdateParams({
                        ...updateParams,
                        firstName: e
                    });
                }}
                style={{
                    marginBottom: spaces.spacingMd
                }}
            />
            <TextInput
                title={localize("lastName")}
                type={updateParams?.lastName !== undefined && updateParams.lastName.trim() === "" ? "danger" : "default"}
                hintText={updateParams?.lastName !== undefined && updateParams.lastName.trim() === "" ? (localize("required-field")) : undefined}
                spreadBehaviour="stretch"
                isDisabled={isProfileUpdateRequest ? true: false}
                placeholder={data.lastName}
                defaultValue={data.lastName}
                onChangeText={(e) => {
                    setData({
                        ...data,
                        lastName: e
                    });
                    setUpdateParams({
                        ...updateParams,
                        lastName: e
                    });
                }}
                style={{
                    marginBottom: spaces.spacingMd
                }}
            />
            <TextInput
                title={localize("emailAddress")}
                type={updateParams?.mail !== undefined && !validator.isEmail(updateParams.mail) ? "danger" : "default"}
                hintText={updateParams?.mail !== undefined && !validator.isEmail(updateParams.mail) ? (localize("invalid-email")) : undefined}
                spreadBehaviour="stretch"
                isDisabled={isProfileUpdateRequest ? true: false}
                rightIconOnPress={() => {
                    startMailVerify();
                }}
                rightIcon={() => {
                    if(data.isMailVerified) {
                        return null;
                    } else {
                        return <View
                            style={{
                                backgroundColor: colors.system.default.info,
                                paddingHorizontal: spaces.spacingMd,
                                paddingVertical:spaces.spacingSm,
                                borderRadius: radiuses.actions
                            }}
                        >
                            <Text
                                color="onPrimary"
                            >
                                {localize("approve")}
                            </Text>
                        </View>;
                    }
                }}
                placeholder={data.mail}
                defaultValue={data.mail}
                onChangeText={(e) => {
                    setData({
                        ...data,
                        mail: e
                    });
                    setUpdateParams({
                        ...updateParams,
                        mail: e
                    });
                }}
                style={{
                    marginBottom: spaces.spacingMd
                }}
            />
            <TextInput
                title={localize("phoneNumber")}
                type={updateParams?.phoneNumber !== undefined && !validator.isMobilePhone(updateParams.phoneNumber) ? "danger" : "default"}
                hintText={updateParams?.phoneNumber !== undefined && !validator.isMobilePhone(updateParams.phoneNumber) ? (localize("invalid-phone")) : undefined}
                spreadBehaviour="stretch"
                isDisabled={isProfileUpdateRequest ? true: false}
                placeholder={data.phoneNumber}
                defaultValue={data.phoneNumber}
                onChangeText={(e) => {
                    setData({
                        ...data,
                        phoneNumber: e
                    });
                    setUpdateParams({
                        ...updateParams,
                        phoneNumber: e
                    });
                }}
                style={{
                    marginBottom: spaces.spacingMd
                }}
            />
            <TextAreaInput
                title={localize("about")}
                spreadBehaviour="stretch"
                isDisabled={isProfileUpdateRequest ? true: false}
                placeholder={data.aboutMyTrade}
                defaultValue={data.aboutMyTrade}
                onChangeText={(e) => {
                    setData({
                        ...data,
                        aboutMyTrade: e
                    });
                    setUpdateParams({
                        ...updateParams,
                        aboutMyTrade: e
                    });
                }}
                maxHeight={500}
                type="default"
                style={{
                    marginBottom: spaces.spacingMd
                }}
            />
        </View>;
    };

    const renderPositions = () => {
        if(data.companies && data.companies.length) {
            return <View
                style={{
                    marginBottom: spaces.spacingXl
                }}
            >
                <Text
                    variant="titleMediumSize"
                    color="high"
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                >
                    {localize("positions")}
                </Text>
                {
                    data.companies.map((e, index)=> {
                        return <PositionCard
                            key={e.companyID}
                            companyLogo={e.companyLogo}
                            companyTitle={e.companyTitle}
                            setUpdateParams={setUpdateParams}
                            updateParams={updateParams}
                            companyID={e.companyID}
                            disabled={isProfileUpdateRequest ? true: false}
                            initialPosition={{
                                _id: e.positionID,
                                value: e.position
                            }}
                            objectIndex={index}
                        />;
                    })
                }
            </View>;
        } else {
            return null;
        }
    };

    const renderActionButtons = () => {
        return <View
            style={[
                stylesheet.actionButtonsContainer,
                {
                    paddingHorizontal: spaces.spacingXl,
                    marginBottom: spaces.spacingLg
                }
            ]}
        >
            <Button
                onPress={() => {
                    if(!isFormValid) {
                        let errorMsg = "Lütfen tüm zorunlu alanları geçerli bir şekilde doldurunuz.";
                        if (!data.firstName || data.firstName.trim() === "") errorMsg = localize("please-enter-first-name");
                        else if (!data.lastName || data.lastName.trim() === "") errorMsg = localize("please-enter-last-name");
                        else if (!data.mail || !validator.isEmail(data.mail)) errorMsg = localize("please-enter-valid-email");
                        else if (!data.phoneNumber || !validator.isMobilePhone(data.phoneNumber)) errorMsg = localize("please-enter-valid-phone");

                        NCoreUIKitToast.open({
                            title: localize("error"),
                            subTitle: errorMsg,
                            type: "danger"
                        });
                        return;
                    }

                    if(isProfileUpdateRequest) {
                        const _user:GetMyProfilePayloadType = JSON.parse(JSON.stringify(user));

                        if(isProfileUpdateRequest.aboutMyTrade) {
                            _user.aboutMyTrade = isProfileUpdateRequest.aboutMyTrade;
                        }

                        if(isProfileUpdateRequest.firstName) {
                            _user.firstName = isProfileUpdateRequest.firstName;
                        }

                        if(isProfileUpdateRequest.lastName) {
                            _user.lastName = isProfileUpdateRequest.lastName;
                        }

                        if(isProfileUpdateRequest.mail) {
                            _user.mail = isProfileUpdateRequest.mail;
                        }

                        if(isProfileUpdateRequest.phoneNumber) {
                            _user.phoneNumber = isProfileUpdateRequest.phoneNumber;
                        }

                        if(isProfileUpdateRequest.companies && isProfileUpdateRequest.companies.length) {

                            _user.companies.map(e => {
                                const _update = isProfileUpdateRequest.companies?.find(x => x.companyID === e.companyID);
                                if(_update) {
                                    e.positionID = _update.positionID;
                                }
                            });
                        }

                        if(isProfileUpdateRequest.profilePhotoKey && isProfileUpdateRequest.profilePhotoKey.length) {
                            _user.profilePhotoURL = isProfileUpdateRequest.profilePhotoKey;
                        }

                        navigation.navigate("EditProfileUpdateRequest", {
                            updateRequest: isProfileUpdateRequest,
                            user: _user
                        });
                    } else {
                        setVerificationDialog(true);
                    }
                }}
                isDisabled={isProfileUpdateRequest ? false : (!isFormValid || (!updateParams && !selctedFile))}
                title={isProfileUpdateRequest ? localize("reviewEditRequest") : localize("submitChangesForApproval")}
                customTextColor={colors.project.brandWhite}
                style={{
                    marginBottom: spaces.spacingMd
                }}
                customBorderColor="black"
                spreadBehaviour="stretch"
                customColor="black"
                size="large"
            />
            <Text
                variant="bodyMediumSize"
                style={{
                    marginBottom: spaces.spacingMd
                }}
            >
                {localize("or")}
            </Text>
            <Button
                onPress={() => {
                    navigation.navigate("DeleteAccount");
                }}
                title={localize("deleteAccount")}
                spreadBehaviour="stretch"
                variant="outline"
                type="danger"
                size="large"
            />
        </View>;
    };

    const renderSuccessDialog = () => {
        return <Dialog
            headerComponent={<View
                style={stylesheet.dialogHeaderContainer}
            >
                <View
                    style={[
                        stylesheet.dialogIconWrapper,
                        {
                            backgroundColor: colors.content.container.success,
                            padding: spaces.spacingMd,
                            borderRadius: radiuses.full,
                            marginBottom: spaces.spacingLg
                        }
                    ]}
                >
                    <Check
                        size={30}
                        color={colors.content.icon.success}
                    />
                </View>
                <Text
                    variant="titleLargeSize"
                    color="high"
                >
                    {localize("submittedSuccessfully")}
                </Text>
            </View>}
            content={localize("changesUnderReview")}
            isVisible={successDialog}
            onOverlayPress={() => {
                setSuccessDialog(false);
                navigation.goBack();
            }}
            primaryButtonProps={{
                onPress: () => {
                    setSuccessDialog(false);
                    navigation.goBack();
                },
                title: localize("backToHome")
            }}
            variant="ok"
        />;
    };

    const renderVerificationDialog = () => {
        return <Dialog
            headerComponent={<View
                style={stylesheet.dialogHeaderContainer}
            >
                <View
                    style={[
                        stylesheet.dialogIconWrapper,
                        {
                            marginBottom: spaces.spacingLg
                        }
                    ]}
                >
                    <CircleQuestionMark
                        size={40}
                        color={colors.content.icon.warning}
                    />
                </View>
                <Text
                    variant="titleLargeSize"
                    color="high"
                    style={stylesheet.dialogTitle}
                >
                    {localize("confirmSubmitForApproval")}
                </Text>
            </View>}
            content={localize("submitForApprovalDescription")}
            isVisible={verificationDialog}
            onOverlayPress={() => {
                setVerificationDialog(false);
            }}
            primaryButtonProps={{
                onPress: () => {
                    setVerificationDialog(false);
                    sendProfileUpdateReq();
                },
                title: localize("submitForApproval"),
                customColor: "black"
            }}
            secondaryButtonProps={{
                title: localize("abandon"),
                onPress: () => {
                    setVerificationDialog(false);
                }
            }}
            variant="yes-no"
        />;
    };

    const verfiyMailSheet = () => {
        return <BottomSheet
            ref={verifyMailRef}
            isAutoHeight={true}
            snapPoint={windowHeight - 100}
            style={{
                borderTopLeftRadius: radiuses.xl,
                borderTopRightRadius: radiuses.xl
            }}
            scrollViewProps={{
                contentContainerStyle: {
                    flex: 1
                }
            }}
            renderHeader={() => <View
                style={[
                    stylesheet.sheetHeaderContainer,
                    {
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <Text
                    variant="titleLargeSize"
                    color="high"
                >
                    {localize("verifyEmailAddress")}
                </Text>
            </View>}
        >
            <VerifyMailCode
                data={data}
                setData={setData}
                onClose={() => {
                    verifyMailRef.current?.close();
                }}
            />
        </BottomSheet>;
    };

    return <PageContainer
        backgroundColor="default"
        isScrollable={true}
        scrollViewProps={{
            showsVerticalScrollIndicator: false
        }}
    >
        {renderProfilePhoto()}
        {renderGeneralInfo()}
        {renderPositions()}
        {renderActionButtons()}
        {renderSuccessDialog()}
        {renderVerificationDialog()}
        {verfiyMailSheet()}
    </PageContainer>;
};
export default EditProfile;
